import { acceptInvite } from '@/services/invites'
import { AppDispatch, Channel, ChannelListProps, RootState } from '@/types'
import Modal from '@components/Modal'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  createChannel,
  fetchChannels,
  updateSelectedChannel,
} from '@store/channels/channels.actions'
import { updateSelectedRoom } from '@store/rooms/rooms.actions'
import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'

const ChannelList: React.FC<ChannelListProps> = ({
  channels,
  fetchChannels,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const [show, setShow] = useState(false)
  const [channelName, setChannelName] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [isJoin, setIsJoin] = useState(false)
  const [channelAvatar, setChannelAvatar] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')

  useEffect(() => {
    fetchChannels()
  }, [fetchChannels])

  const handleChannelClick = (channelId: string | undefined) => {
    if (!channelId) return
    dispatch(updateSelectedChannel(channelId))
    dispatch(updateSelectedRoom(null))
  }

  const addChannel = () => {
    setShow(true)
  }

  // TODO: handle errors
  const handleJoin = async () => {
    if (!inviteCode) return

    try {
      await acceptInvite(inviteCode)
      dispatch(fetchChannels())
    } catch (e) {
      console.log(e)
    }

    setInviteCode('')
    setShow(false)
  }

  const handleCreate = () => {
    const formData = new FormData()
    if (!channelAvatar || !channelName) return

    formData.append('file', channelAvatar)
    formData.append('name', channelName)

    setChannelName('')
    setChannelAvatar(null)
    dispatch(createChannel(formData))
    setShow(false)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setChannelAvatar(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  return (
    <div className="w-full h-full bg-gray-900 text-white">
      <nav
        className="flex flex-col space-y-2 absolute
	    w-full
	    top-1/2
	    transform
	    -translate-y-1/2
	"
      >
        <button
          className="
	  mx-auto
	  text-2xl
	  p-2
	  h-[51px]
	  w-[51px]
	  bg-secondary
	  rounded-full
	  hover:bg-secondary-light"
          onClick={addChannel}
        >
          <h3>
            <FontAwesomeIcon icon={faPlus} color="#fff" />
          </h3>
        </button>
        {channels?.channels.map((channel: Channel) => (
          <button
            key={channel.id}
            className={`mx-auto text-2xl font-bold p-2 rounded-full h-[51px] w-[51px] text-white text-center ${
              channels.selectedChannelId === channel.id
                ? 'ring-4 ring-primary'
                : ''
            }`}
            style={{
              backgroundImage: `url(data:image/png;base64,${channel.avatar})`,
              backgroundPosition: 'center',
            }}
            onClick={() => handleChannelClick(channel.id)}
          ></button>
        ))}
      </nav>
      <Modal
        title="Create or Join a Channel"
        show={show}
        handleClose={() => setShow(false)}
      >
        <div className="p-0">
          <div className="flex items-center p-4">
            <span className={isJoin ? 'text-grey-400 m-0' : 'hidden'}>
              Join
            </span>
            <span className={!isJoin ? 'text-grey-400 m-0' : 'hidden'}>
              Create
            </span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isJoin}
                onChange={() => setIsJoin(!isJoin)}
                className="sr-only"
              />
              <div className="w-11 h-6 ml-4 bg-gray-200 rounded-full peer dark:bg-gray-700">
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                    isJoin ? 'translate-x-0' : 'translate-x-5'
                  }`}
                ></div>
              </div>
            </label>
          </div>
          {isJoin ? (
            <div className="p-4">
              <h2 className="font-semibold">Join Channel</h2>
              <div className="p-2">
                <label className="block mb-2">Code</label>
                <input
                  type="text"
                  placeholder="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  className="w-full p-2 mb-4 border"
                />
                <button
                  onClick={() => handleJoin()}
                  className="
			bg-primary
			p-2
			my-2
			text-secondary
			text-lg
			rounded
			cursor-pointer
			hover:bg-primary-dark
			"
                >
                  Join
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <h2 className="font-semibold">Create Channel</h2>
              <div className="p-2">
                <label className="block mb-2">Channel Name</label>
                <input
                  type="text"
                  placeholder="Channel Name"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  className="w-full p-2 mb-4 border"
                />
                <label className="block mb-2">Channel Avatar</label>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full p-2 mb-4 border"
                />
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Channel Avatar Preview"
                    className="w-32 h-32 mb-4 rounded-full object-cover"
                  />
                )}
                <button
                  onClick={() => handleCreate()}
                  className="
					bg-primary
					p-2
					my-2
					text-secondary
					text-lg
					rounded
					cursor-pointer
					hover:bg-primary-dark
					"
                >
                  Create
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  channels: state.channels,
})

const mapDispatchToProps = {
  fetchChannels,
}

const ConnectedChannelList = connect(
  mapStateToProps,
  mapDispatchToProps
)(ChannelList)

export default ConnectedChannelList
